import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { toast } from "sonner";

export default function Notebook() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [notebooks, setNotebooks] = useState([]);
  const [active, setActive] = useState(null);
  const [sources, setSources] = useState([]);
  const [qa, setQa] = useState([]);
  const [q, setQ] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadNotebooks = useCallback(() => api.get("/notebook/notebooks").then((r) => setNotebooks(r.data)).catch(() => {}), []);
  useEffect(() => { loadNotebooks(); }, [loadNotebooks]);

  const openNb = useCallback(async (nb) => {
    setActive(nb); setSources([]); setQa([]);
    const [s, h] = await Promise.all([
      api.get(`/notebook/notebooks/${nb.id}/sources`),
      api.get(`/notebook/notebooks/${nb.id}/qa`),
    ]);
    setSources(s.data); setQa(h.data.map((x, i) => ({ ...x, id: "h" + i })));
  }, []);

  const createNb = async () => {
    const title = newTitle.trim() || "Untitled notebook";
    const { data } = await api.post("/notebook/notebooks", { title });
    setNewTitle(""); await loadNotebooks(); openNb(data);
    toast.success("Notebook created");
  };

  const delNb = async (nb, e) => {
    e.stopPropagation();
    await api.delete(`/notebook/notebooks/${nb.id}`);
    if (active?.id === nb.id) { setActive(null); setSources([]); setQa([]); }
    loadNotebooks(); toast.success("Notebook deleted");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !active) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/notebook/notebooks/${active.id}/sources`, fd);
      setSources((s) => [...s, data]); loadNotebooks();
      toast.success(`Added ${data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false); if (fileRef.current) fileRef.current.value = "";
    }
  };

  const delSource = async (sid) => {
    await api.delete(`/notebook/sources/${sid}`);
    setSources((s) => s.filter((x) => x.id !== sid)); loadNotebooks();
  };

  const ask = async () => {
    if (!q.trim() || !active) return;
    if (sources.length === 0) return toast.error("Add at least one source first");
    const question = q; setQ(""); setBusy(true);
    const qid = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    setQa((h) => [...h, { id: qid, question, answer: "", pending: true }]);
    try {
      const { data } = await api.post(`/notebook/notebooks/${active.id}/ask`, { question });
      setQa((h) => h.map((x) => (x.pending ? { id: x.id, question, answer: data.answer } : x)));
      refreshUser();
    } catch (err) {
      setQa((h) => h.filter((x) => !x.pending));
      if (err.response?.status === 402) toast.error("Insufficient credits. Please top up.");
      else toast.error(err.response?.data?.detail || "Couldn't answer");
      setQ(question);
    } finally { setBusy(false); }
  };

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Research Notebook" icon="NotebookPen" color="#8B5CF6"
        subtitle="Upload factsheets, circulars or notes and ask questions — answered only from your sources, with citations." />

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Notebooks list */}
        <div className="space-y-4">
          <Card data-testid="notebook-list">
            <h3 className="font-head font-semibold text-sm mb-3 flex items-center gap-2"><Icon name="Notebook" size={16} className="text-slate-400" /> Your notebooks</h3>
            <div className="flex gap-2 mb-3">
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createNb()}
                data-testid="notebook-title-input" placeholder="New notebook name"
                className="flex-1 rounded-xl bg-[#141822] border border-[#2E384D] px-3 py-2 text-sm outline-none focus:border-violet-500/40 text-slate-100" />
              <button onClick={createNb} data-testid="notebook-create-btn" className="grad-blue-violet text-white rounded-xl px-3 py-2 text-sm font-semibold"><Icon name="Plus" size={16} /></button>
            </div>
            {notebooks.length === 0 && <p className="text-sm text-slate-500">No notebooks yet. Create one to get started.</p>}
            <div className="space-y-1">
              {notebooks.map((nb) => (
                <button key={nb.id} onClick={() => openNb(nb)} data-testid="notebook-item"
                  className={`group w-full text-left rounded-xl px-3 py-2.5 flex items-center gap-2 transition-colors ${active?.id === nb.id ? "bg-violet-500/12" : "hover:bg-[#1A1F2B]"}`}>
                  <Icon name="NotebookPen" size={15} className="text-violet-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{nb.title}</div>
                    <div className="text-[11px] text-slate-500">{nb.sources} source{nb.sources === 1 ? "" : "s"}</div>
                  </div>
                  <span onClick={(e) => delNb(nb, e)} data-testid="notebook-delete-btn" className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400"><Icon name="Trash2" size={14} /></span>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Active notebook */}
        <div className="lg:col-span-2 space-y-5">
          {!active ? (
            <Card className="grid place-items-center text-center py-16" data-testid="notebook-empty">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-violet-500/12 grid place-items-center mx-auto mb-3"><Icon name="NotebookPen" size={26} className="text-violet-400" /></div>
                <p className="text-slate-400 text-sm">Select or create a notebook, add your sources, then ask anything.</p>
              </div>
            </Card>
          ) : (
            <>
              {/* Sources */}
              <Card data-testid="notebook-sources">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-head font-semibold text-sm flex items-center gap-2"><Icon name="Files" size={16} className="text-slate-400" /> Sources</h3>
                  <label data-testid="notebook-upload-label" className="cursor-pointer text-xs font-semibold rounded-lg px-3 py-1.5 grad-blue-violet text-white inline-flex items-center gap-1.5">
                    <Icon name={uploading ? "Loader2" : "Upload"} size={14} className={uploading ? "animate-spin" : ""} /> {uploading ? "Reading…" : "Upload"}
                    <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.csv" onChange={upload} data-testid="notebook-upload-input" className="hidden" disabled={uploading} />
                  </label>
                </div>
                {sources.length === 0 ? (
                  <p className="text-sm text-slate-500">Upload a PDF or text file (factsheet, SEBI circular, portfolio notes) to ground your answers.</p>
                ) : (
                  <div className="space-y-1.5">
                    {sources.map((s) => (
                      <div key={s.id} className="flex items-center gap-2 rounded-xl border border-[#222938] px-3 py-2" data-testid="notebook-source-item">
                        <Icon name="FileText" size={15} className="text-violet-400 shrink-0" />
                        <span className="text-sm truncate flex-1">{s.name}</span>
                        <span className="text-[11px] text-slate-500">{(s.chars / 1000).toFixed(1)}k chars</span>
                        <button onClick={() => delSource(s.id)} data-testid="notebook-source-delete" className="text-slate-500 hover:text-red-400"><Icon name="X" size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Q&A */}
              <Card data-testid="notebook-qa">
                <h3 className="font-head font-semibold text-sm mb-3 flex items-center gap-2"><Icon name="Sparkles" size={16} className="text-violet-400" /> Ask your sources <span className="text-[11px] text-slate-500 font-normal">· ₹2 / question · Gemini</span></h3>
                <div className="space-y-4 mb-4 max-h-[38vh] overflow-y-auto">
                  {qa.length === 0 && <p className="text-sm text-slate-500">Ask a question and I'll answer using only your uploaded sources, with citations.</p>}
                  {qa.map((item, i) => (
                    <div key={item.id || i} className="space-y-2" data-testid="notebook-qa-item">
                      <div className="flex justify-end"><div className="max-w-[80%] rounded-2xl px-4 py-2.5 grad-blue-violet text-white text-sm">{item.question}</div></div>
                      <div className="flex justify-start"><div className="max-w-[85%] rounded-2xl px-4 py-2.5 bg-[#1A1F2B] text-slate-100 text-sm whitespace-pre-wrap">{item.pending ? <span className="inline-flex items-center gap-2 text-slate-400"><Icon name="Loader2" size={14} className="animate-spin" /> Reading your sources…</span> : item.answer}</div></div>
                      {!item.pending && item.answer && (
                        <div className="flex justify-start">
                          <button onClick={() => nav("/app/content", { state: { topic: item.answer } })} data-testid="notebook-save-content-btn"
                            className="text-[11px] font-semibold text-violet-400 hover:text-violet-300 inline-flex items-center gap-1 ml-1">
                            <Icon name="Megaphone" size={12} /> Save to Content Studio
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !busy && ask()}
                    data-testid="notebook-question-input" placeholder="e.g. What is the expense ratio and risk level?"
                    className="flex-1 rounded-xl bg-[#141822] border border-[#2E384D] px-3 py-2.5 text-sm outline-none focus:border-violet-500/40 text-slate-100" />
                  <Btn onClick={ask} disabled={busy} data-testid="notebook-ask-btn"><Icon name={busy ? "Loader2" : "Send"} size={16} className={busy ? "animate-spin" : ""} /> Ask</Btn>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
